import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertTriangle, AlertCircle, Search, Clock, CheckCircle2, XCircle, 
  RefreshCw, History, Calendar, TrendingUp, Timer, Download
} from 'lucide-react';
import { formatDate, formatDuration, SEVERITY_COLORS, SEVERITY_LABELS } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_PERIODS = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '14', label: 'Últimos 14 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '60', label: 'Últimos 60 dias' },
  { value: '90', label: 'Últimos 90 dias' },
];

function SeverityBadge({ severity }) {
  return (
    <Badge 
      className="text-white text-xs"
      style={{ backgroundColor: SEVERITY_COLORS[severity] }}
    >
      {SEVERITY_LABELS[severity]}
    </Badge>
  );
}

function StatusBadge({ status }) {
  if (status === 'resolved') {
    return (
      <Badge className="bg-green-500 text-white text-xs">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        Resolvido
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-500 text-white text-xs">
      <AlertCircle className="h-3 w-3 mr-1" />
      Ativo
    </Badge>
  );
}

function StatsCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TimelineDay({ day }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b last:border-0">
      <div className="w-28 flex-shrink-0">
        <p className="text-sm font-medium">{new Date(day.date).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' })}</p>
      </div>
      <div className="flex-1 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <span className="text-sm font-bold">{day.total}</span>
          </div>
          <span className="text-xs text-muted-foreground">total</span>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span className="text-sm">{day.resolved}</span>
        </div>
        {day.active > 0 && (
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm">{day.active}</span>
          </div>
        )}
        <div className="flex-1 flex justify-end gap-1">
          {Object.entries(day.by_severity || {}).map(([sev, count]) => (
            <Badge 
              key={sev} 
              variant="secondary" 
              className="text-[10px]"
              style={{ 
                backgroundColor: SEVERITY_COLORS[Object.keys(SEVERITY_LABELS).find(k => SEVERITY_LABELS[k] === sev)] + '30',
                color: SEVERITY_COLORS[Object.keys(SEVERITY_LABELS).find(k => SEVERITY_LABELS[k] === sev)]
              }}
            >
              {sev}: {count}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

export function IncidentHistoryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [period, setPeriod] = useState('7');
  const [statusFilter, setStatusFilter] = useState('all');
  const { getAuthHeader } = useAuthStore();
  const { refreshInterval } = useThemeStore();

  // Apply saved filter from sessionStorage
  useEffect(() => {
    try {
      const savedFilter = sessionStorage.getItem('appliedFilter');
      if (savedFilter) {
        const f = JSON.parse(savedFilter);
        if (f.search) setSearchTerm(f.search);
        if (f.period) setPeriod(f.period);
        if (f.statusFilter) setStatusFilter(f.statusFilter);
        sessionStorage.removeItem('appliedFilter');
      }
    } catch (err) {
      console.warn('Filtro salvo inválido, ignorando:', err);
      sessionStorage.removeItem('appliedFilter');
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/incidents/history?days=${period}`, { 
        headers: getAuthHeader() 
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply filters to incidents
  const filteredIncidents = data?.incidents?.filter(incident => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch = 
        incident.name.toLowerCase().includes(term) ||
        incident.host_name.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }
    
    // Status filter
    if (statusFilter !== 'all' && incident.status !== statusFilter) {
      return false;
    }
    
    return true;
  }) || [];

  // Calculate filtered stats for cards - these update when filters change
  const filteredStats = {
    total: filteredIncidents.length,
    resolved: filteredIncidents.filter(i => i.status === 'resolved').length,
    active: filteredIncidents.filter(i => i.status === 'active').length,
  };
  
  // Calculate average resolution time from filtered resolved incidents
  const resolvedIncidents = filteredIncidents.filter(i => i.status === 'resolved' && i.duration_seconds);
  const avgResolutionSeconds = resolvedIncidents.length > 0 
    ? resolvedIncidents.reduce((sum, i) => sum + i.duration_seconds, 0) / resolvedIncidents.length
    : 0;
  
  // Format average resolution time
  const formatAvgTime = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (!filteredIncidents.length) return;
    
    const headers = ['Status', 'Severidade', 'Host', 'Problema', 'Início', 'Fim', 'Duração', 'ACK'];
    const rows = filteredIncidents.map(inc => [
      inc.status === 'resolved' ? 'Resolvido' : 'Ativo',
      inc.severity_label,
      inc.host_name,
      `"${inc.name.replace(/"/g, '""')}"`,  // Escape quotes in CSV
      inc.start_time ? new Date(inc.start_time).toLocaleString('pt-BR') : '-',
      inc.end_time ? new Date(inc.end_time).toLocaleString('pt-BR') : 'Em aberto',
      inc.duration,
      inc.acknowledged ? 'Sim' : 'Não'
    ]);
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `historico_incidentes_${period}dias_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))}
        </div>
        <Skeleton className="h-[500px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="incident-history-page">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]" data-testid="period-filter">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="resolved">Resolvidos</SelectItem>
              <SelectItem value="active">Ativos</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={exportToCSV} disabled={!filteredIncidents.length}>
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <RefreshCw className="h-3 w-3" />
          <span>Período: {period} dias</span>
        </div>
      </div>

      {/* Stats Cards - now show FILTERED values */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard 
          title="Total de Incidentes" 
          value={filteredStats.total} 
          subtitle={`nos últimos ${period} dias`}
          icon={History} 
          color="bg-blue-500"
        />
        <StatsCard 
          title="Resolvidos" 
          value={filteredStats.resolved} 
          subtitle={`${filteredStats.total ? Math.round((filteredStats.resolved / filteredStats.total) * 100) : 0}% do total`}
          icon={CheckCircle2} 
          color="bg-green-500"
        />
        <StatsCard 
          title="Ainda Ativos" 
          value={filteredStats.active} 
          subtitle="aguardando resolução"
          icon={AlertTriangle} 
          color="bg-red-500"
        />
        <StatsCard 
          title="Tempo Médio (MTTR)" 
          value={formatAvgTime(avgResolutionSeconds)} 
          subtitle="de resolução"
          icon={Timer} 
          color="bg-purple-500"
        />
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="list" className="w-full">
        <TabsList>
          <TabsTrigger value="list">Lista de Incidentes</TabsTrigger>
          <TabsTrigger value="timeline">Timeline por Dia</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Histórico de Incidentes
                  <Badge variant="outline" className="ml-2 font-mono">
                    {filteredIncidents.length} registros
                  </Badge>
                </CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar no histórico..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-history"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[90px]">Status</TableHead>
                      <TableHead className="w-[100px]">Severidade</TableHead>
                      <TableHead>Host</TableHead>
                      <TableHead>Problema</TableHead>
                      <TableHead className="w-[150px]">Início</TableHead>
                      <TableHead className="w-[150px]">Fim</TableHead>
                      <TableHead className="w-[100px]">Duração</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredIncidents.length > 0 ? (
                      filteredIncidents.map((incident) => (
                        <TableRow 
                          key={incident.id}
                          className={incident.status === 'active' ? 'bg-red-500/5' : ''}
                          data-testid={`history-row-${incident.id}`}
                        >
                          <TableCell>
                            <StatusBadge status={incident.status} />
                          </TableCell>
                          <TableCell>
                            <SeverityBadge severity={incident.severity} />
                          </TableCell>
                          <TableCell className="font-mono text-sm">{incident.host_name}</TableCell>
                          <TableCell>
                            <p className="font-medium text-sm truncate max-w-md">{incident.name}</p>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(incident.start_time)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {incident.end_time ? (
                              <span className="text-green-600">{formatDate(incident.end_time)}</span>
                            ) : (
                              <span className="text-red-500 font-medium">Em aberto</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Clock className="h-3 w-3" />
                              {formatDuration(incident.duration)}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                          Nenhum incidente encontrado no período
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Timeline por Dia
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {data?.timeline?.length > 0 ? (
                  <div className="space-y-1">
                    {data.timeline.map((day) => (
                      <TimelineDay key={day.date} day={day} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    Nenhum dado disponível para o período
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
